import { useState, useRef, useEffect } from "react";
import "./UR.css";

/* ═══════════════════════════════════════════════════════════════
   ATSScanner.jsx  ·  AI-Powered ATS Resume Analyzer
   - Tries your backend first (server-side pdf-parse / mammoth)
   - Falls back to Gemini direct if backend is unreachable
   - Works in both local dev and on the hosted server
═══════════════════════════════════════════════════════════════ */

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || "AIzaSyDOB_HGTm24DvWS46cqeKrjQkrMareZans";
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`;

// On host: REACT_APP_API_URL=http://indianhospitalitynetwork.in
// Locally: falls back to localhost:3000
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:3000").replace(/\/$/, "");

const ATS_GLOSSARY = {
  "ATS": "Applicant Tracking System — software used by employers to filter and rank resumes before a human reviews them.",
  "ATS Score": "A numeric rating (0–100) estimating how well your resume will perform in automated screening systems.",
  "Keywords": "Specific words or phrases from job descriptions that ATS systems search for in your resume.",
  "Parsing": "The process by which ATS software reads and extracts data from your resume.",
  "Quantified Achievements": "Work accomplishments described with numbers or metrics, e.g. 'increased sales by 30%'.",
  "Professional Summary": "A 3–5 line statement at the top of your CV that provides context about your experience and goals.",
  "Skills Section": "A dedicated section listing technical and soft skills, parsed separately by ATS systems.",
  "Certifications": "Professional credentials from recognized bodies that add credibility and keyword weight.",
  "LinkedIn URL": "A link to your LinkedIn profile — expected by most modern ATS systems.",
  "Single Column Layout": "A resume format without tables or columns, which ATS parsers can read most reliably.",
  "CI/CD": "Continuous Integration/Continuous Deployment — a software engineering practice for automated testing and deployment.",
  "Kubernetes": "An open-source system for automating deployment and scaling of containerized applications.",
  "Terraform": "Infrastructure-as-Code tool used to provision cloud resources.",
  "Microservices": "An architecture pattern where applications are built as small, independent services.",
  "GraphQL": "A query language for APIs, an alternative to REST.",
  "Scrum": "An agile framework for managing and completing complex projects in iterative sprints.",
  "Docker": "A platform for building and running applications in containers.",
  "AWS": "Amazon Web Services — a leading cloud computing platform.",
  "REST API": "Representational State Transfer API — a standard way for web services to communicate.",
  "Agile": "An iterative project management methodology focused on collaboration and flexibility.",
  "DevOps": "Practices combining software development and IT operations to shorten the development lifecycle.",
  "Machine Learning": "A subset of AI where systems learn from data to improve performance without explicit programming.",
  "TypeScript": "A typed superset of JavaScript that compiles to plain JavaScript.",
  "System Design": "The process of defining architecture, components, and interfaces for a system to satisfy specified requirements.",
};

/* ── File readers ── */
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => resolve(e.target.result.split(",")[1]);
    reader.readAsDataURL(file);
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => resolve(e.target.result);
    reader.readAsText(file);
  });
}

/* ── Try backend server first (uses pdf-parse / mammoth server-side) ── */
async function analyzeWithBackend(file, jobTitle) {
  const base64Data = await readFileAsBase64(file);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15001);
  try {
    const res = await fetch(`${API_BASE}/api/ats/analyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fileName: file.name,
        fileData: base64Data,
        fileType: file.type,
        jobTitle: jobTitle || "",
      }),
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) throw new Error(`Backend HTTP ${res.status}`);
    const apiData = await res.json();
    const parsed = apiData.result || apiData;
    if (!parsed || typeof parsed.score !== "number") throw new Error("Bad backend response");
    return parsed;
  } catch (e) {
    clearTimeout(timeout);
    throw e;
  }
}

/* ── Gemini direct (fallback for local dev or when backend is down) ── */
async function analyzeWithGemini(file, jobTitle) {
  const fileName = file.name.toLowerCase();
  let parts = [];

  const prompt = `You are an expert ATS (Applicant Tracking System) resume analyzer. Analyze the provided resume and return ONLY a valid JSON object with NO markdown, NO code blocks, NO extra text.

Job Title Target: ${jobTitle || "General/Not specified"}

Analyze the resume thoroughly and return this exact JSON structure:
{
  "score": <number 0-100>,
  "headline": "<short verdict like 'Strong Resume' or 'Needs Improvement'>",
  "description": "<2-3 sentence overall assessment>",
  "detectedRole": "<detected job role from resume>",
  "experienceYears": "<e.g. '5+ years' or 'Entry level'>",
  "topStrength": "<single biggest strength in one sentence>",
  "criticalFix": "<single most important thing to fix>",
  "pillars": [
    {"name": "Keywords", "val": <0-100>},
    {"name": "Formatting", "val": <0-100>},
    {"name": "Achievements", "val": <0-100>},
    {"name": "Completeness", "val": <0-100>}
  ],
  "good": [
    {"text": "<what is working>", "sub": "<brief explanation>"},
    {"text": "<what is working>", "sub": "<brief explanation>"},
    {"text": "<what is working>", "sub": "<brief explanation>"}
  ],
  "warnings": [
    {"text": "<issue found>", "sub": "<why it matters>"},
    {"text": "<issue found>", "sub": "<why it matters>"},
    {"text": "<issue found>", "sub": "<why it matters>"}
  ],
  "missing": [
    {"name": "<missing element>", "priority": "critical", "desc": "<what is missing>", "tip": "<how to fix it>"},
    {"name": "<missing element>", "priority": "high", "desc": "<what is missing>", "tip": "<how to fix it>"},
    {"name": "<missing element>", "priority": "medium", "desc": "<what is missing>", "tip": "<how to fix it>"}
  ],
  "keywordsFound": ["<up to 12 real keywords found in the resume>"],
  "keywordsMissing": ["<up to 10 high-value keywords to add>"]
}

Be specific and accurate. Extract REAL keywords from the resume text. Score honestly based on ATS best practices.`;

  if (fileName.endsWith(".txt")) {
    const text = await readFileAsText(file);
    parts = [{ text: prompt + "\n\nRESUME TEXT:\n" + text }];
  } else {
    const base64 = await readFileAsBase64(file);
    parts = [
      { text: prompt },
      { inline_data: { mime_type: file.type || "application/pdf", data: base64 } },
    ];
  }

  const res = await fetch(GEMINI_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      contents: [{ parts }],
      generationConfig: { temperature: 0.3, maxOutputTokens: 2048 },
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Gemini API error: ${res.status}`);
  }

  const data = await res.json();
  const rawText = data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
  const cleaned = rawText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

  try {
    return JSON.parse(cleaned);
  } catch {
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (match) return JSON.parse(match[0]);
    throw new Error("Failed to parse AI response. Please try again.");
  }
}

/* ── Smart router: backend first → Gemini direct fallback ── */
async function analyzeResume(file, jobTitle, onStatusUpdate) {
  try {
    onStatusUpdate?.("Connecting to analysis server…");
    const result = await analyzeWithBackend(file, jobTitle);
    return { result, source: "backend" };
  } catch (err) {
    console.warn("Backend unavailable, falling back to Gemini direct:", err.message);
  }
  onStatusUpdate?.("Using direct AI analysis…");
  const result = await analyzeWithGemini(file, jobTitle);
  return { result, source: "gemini" };
}

function formatFileSize(bytes) {
  if (bytes < 1048576) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function badgeClass(priority) {
  return (
    { critical: "ats-badge-critical", high: "ats-badge-high", medium: "ats-badge-medium" }[priority] ||
    "ats-badge-medium"
  );
}

const LOAD_MSGS = [
  "Uploading your document…",
  "Extracting text from resume…",
  "Running ATS compatibility checks…",
  "Analyzing keyword density…",
  "Scoring against industry benchmarks…",
  "Generating personalized recommendations…",
];

/* ── Tooltip ── */
function Tooltip({ text, children }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = useRef();

  function updatePos() {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) setPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
  }

  return (
    <span
      ref={ref}
      className="ats-tooltip-wrap"
      onMouseEnter={() => { setVisible(true); updatePos(); }}
      onMouseMove={updatePos}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <span className="ats-tooltip-bubble" style={{ left: pos.x, top: pos.y }}>
          {text}
        </span>
      )}
    </span>
  );
}

/* ── Highlighted Text with glossary tooltips ── */
function HighlightedText({ text }) {
  if (!text || typeof text !== "string") return <span>{text}</span>;
  const terms = Object.keys(ATS_GLOSSARY).sort((a, b) => b.length - a.length);
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    let matched = false;
    for (const term of terms) {
      if (remaining.toLowerCase().startsWith(term.toLowerCase())) {
        parts.push(
          <Tooltip key={key++} text={ATS_GLOSSARY[term]}>
            <span className="ats-term">{remaining.slice(0, term.length)}</span>
          </Tooltip>
        );
        remaining = remaining.slice(term.length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      let nextIdx = remaining.length;
      for (const term of terms) {
        const idx = remaining.toLowerCase().indexOf(term.toLowerCase(), 1);
        if (idx > 0 && idx < nextIdx) nextIdx = idx;
      }
      parts.push(<span key={key++}>{remaining.slice(0, nextIdx)}</span>);
      remaining = remaining.slice(nextIdx);
    }
  }
  return <span>{parts}</span>;
}

/* ── Animated Score Ring ── */
function ScoreRing({ score }) {
  const [displayed, setDisplayed] = useState(0);
  const circumference = 415;
  const offset = circumference - (displayed / 100) * circumference;
  const color = score >= 75 ? "#15803d" : score >= 55 ? "#263012" : "#e03d4e";

  useEffect(() => {
    let cur = 0;
    const iv = setInterval(() => {
      cur = Math.min(cur + 1, score);
      setDisplayed(cur);
      if (cur >= score) clearInterval(iv);
    }, 24);
    return () => clearInterval(iv);
  }, [score]);

  return (
    <div className="ats-score-ring-wrap">
      <svg className="ats-score-ring-svg" viewBox="0 0 160 160">
        <circle className="ats-score-ring-bg" cx="80" cy="80" r="66" />
        <circle
          className="ats-score-ring-fill"
          cx="80" cy="80" r="66"
          stroke={color}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1.6s cubic-bezier(0.4,0,0.2,1), stroke 0.5s" }}
        />
      </svg>
      <div className="ats-score-center">
        <div className="ats-score-val" style={{ color }}>{displayed}</div>
        <div className="ats-score-denom" style={{ color }}>/100</div>
        <div className="ats-score-lbl">ATS Score</div>
      </div>
    </div>
  );
}

/* ── Animated Pillar Bar ── */
function PillarBar({ name, val, delay }) {
  const [width, setWidth] = useState(0);
  const color = val >= 70 ? "#15803d" : val >= 50 ? "#263012" : "#e03d4e";

  useEffect(() => {
    const t = setTimeout(() => setWidth(val), 300 + delay);
    return () => clearTimeout(t);
  }, [val, delay]);

  return (
    <div>
      <div className="ats-pillar-name">{name}</div>
      <div className="ats-pillar-track">
        <div className="ats-pillar-fill" style={{ width: `${width}%`, background: color }} />
      </div>
      <div className="ats-pillar-val" style={{ color }}>{val}%</div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
export default function UR() {
  const [file, setFile] = useState(null);
  const [jobTitle, setJobTitle] = useState("");
  const [phase, setPhase] = useState("idle");
  const [data, setData] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [expandedMissing, setExpandedMissing] = useState({});

  const fileRef = useRef();
  const resultsRef = useRef();

  async function runAnalysis() {
    if (!file) return;
    setPhase("loading");
    setErrMsg("");
    setExpandedMissing({});

    let msgIdx = 0;
    setLoadMsg(LOAD_MSGS[0]);
    const msgTimer = setInterval(() => {
      msgIdx = Math.min(msgIdx + 1, LOAD_MSGS.length - 1);
      setLoadMsg(LOAD_MSGS[msgIdx]);
    }, 2200);

    try {
      const { result, source } = await analyzeResume(file, jobTitle, (msg) => setLoadMsg(msg));
      clearInterval(msgTimer);

      if (!result || typeof result.score !== "number") {
        throw new Error("Unexpected response from AI. Please try again.");
      }

      setData({ ...result, _source: source });
      setPhase("results");
      setTimeout(
        () => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        120
      );
    } catch (e) {
      clearInterval(msgTimer);
      setErrMsg(e.message || "Analysis failed. Please try again.");
      setPhase("error");
    }
  }

  function reset() {
    setFile(null); setJobTitle(""); setPhase("idle");
    setData(null); setErrMsg(""); setExpandedMissing({});
    if (fileRef.current) fileRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function downloadReport() {
    if (!data) return;
    const lines = [
      "═══════════════════════════════════════════",
      "            ATS SCAN REPORT",
      "═══════════════════════════════════════════",
      `File      : ${file?.name}`,
      `Job Title : ${jobTitle || "Not specified"}`,
      `Score     : ${data.score}/100`,
      `Verdict   : ${data.headline}`,
      `Source    : ${data._source === "backend" ? "Server Analysis" : "Direct AI Analysis"}`, "",
      data.description, "",
      "─── PILLAR SCORES ──────────────────────────",
      ...(data.pillars?.map((p) => `  ${p.name.padEnd(12)}: ${p.val}%`) || []), "",
      "─── WHAT'S WORKING ─────────────────────────",
      ...(data.good?.map((g, i) => `  ${i + 1}. ${g.text}\n     ${g.sub}`) || []), "",
      "─── NEEDS ATTENTION ────────────────────────",
      ...(data.warnings?.map((w, i) => `  ${i + 1}. ${w.text}\n     ${w.sub}`) || []), "",
      "─── MISSING ELEMENTS ───────────────────────",
      ...(data.missing?.map((m, i) => `  ${i + 1}. [${m.priority.toUpperCase()}] ${m.name}\n     ${m.desc}\n     FIX: ${m.tip}`) || []), "",
      "─── KEYWORDS FOUND ─────────────────────────",
      "  " + (data.keywordsFound?.join(", ") || ""), "",
      "─── KEYWORDS TO ADD ────────────────────────",
      "  " + (data.keywordsMissing?.join(", ") || ""), "",
      `Top Strength : ${data.topStrength || ""}`,
      `Critical Fix : ${data.criticalFix || ""}`, "",
      "Generated by ATSScan — AI-Powered Resume Analyzer",
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob),
      download: `ats-report-${file?.name?.replace(/\.[^.]+$/, "") || "resume"}.txt`,
    });
    a.click();
  }

  function toggleMissing(i) {
    setExpandedMissing((prev) => ({ ...prev, [i]: !prev[i] }));
  }

  return (
    <div className="ats-app">
      <div className="ats-container">

        {/* Hero */}
        <section className="ats-hero" data-aos="fade-up">
          <h1>Is Your CV <span className="choose-title-accent">ATS Proof?</span></h1>
          <p>
            Upload your resume and get a real AI-powered ATS score — actual keywords
            extracted from your document, exact fixes, and personalized recommendations.
          </p>
        </section>

        {/* Two-column layout */}
        <div className="ats-two-col">

          {/* ── LEFT COLUMN ── */}
          <div className="ats-left-col" data-aos="fade-left">

            {/* Upload Zone */}
            <div
              className={`ats-upload-zone${dragOver ? " dragover" : ""}`}
              data-aos="fade-left"
              data-aos-delay="100"
              onClick={() => fileRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
              }}
            >
              <input
                type="file" ref={fileRef} accept=".pdf,.doc,.docx,.txt"
                style={{ display: "none" }}
                onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }}
              />
              <div className="ats-upload-icon">📄</div>
              <div className="ats-upload-title">Drop your CV here to analyze</div>
              <div className="ats-upload-sub">Drag & drop or click to choose your file</div>
              <button
                className="ats-upload-btn"
                onClick={(e) => { e.stopPropagation(); fileRef.current.click(); }}
              >
                ↑&nbsp; Choose File
              </button>
              <div className="ats-upload-formats">Supports PDF · DOC · DOCX · TXT</div>

              {file && (
                <div className="ats-file-pill" onClick={(e) => e.stopPropagation()}>
                  <span className="ats-file-icon">📋</span>
                  <div className="ats-file-pill-info">
                    <div className="ats-file-name">{file.name}</div>
                    <div className="ats-file-size">{formatFileSize(file.size)} · Ready to analyze</div>
                  </div>
                  <button
                    className="ats-file-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                  >✕</button>
                </div>
              )}
            </div>

            {/* Job Title + Analyze */}
            <div className="ats-job-section" data-aos="fade-left" data-aos-delay="180">
              <div className="ats-section-label">Target Job Title — optional</div>
              <div className="ats-job-row">
                <input
                  className="ats-job-input"
                  type="text"
                  placeholder="e.g. Senior Software Engineer…"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && file && phase !== "loading" && runAnalysis()}
                />
              </div>
              <button
                className="ats-analyze-btn-full"
                disabled={!file || phase === "loading"}
                onClick={runAnalysis}
              >
                {phase === "loading" ? (
                  <span className="ats-btn-loading">
                    <span className="ats-btn-spinner" /> Analyzing…
                  </span>
                ) : "Analyze CV →"}
              </button>
            </div>

            {/* Info bar */}
            <div className="ats-info-bar" data-aos="fade-left" data-aos-delay="240">
              <span>🔒</span>
              Your resume is processed securely and never stored permanently.
              Hover over any <span className="term-hint">underlined term</span> for its definition.
            </div>

          </div>
          {/* END LEFT */}

          {/* ── RIGHT COLUMN ── */}
          <div className="ats-right-col" ref={resultsRef} data-aos="fade-right">

            {/* Idle placeholder */}
            {phase === "idle" && (
              <div className="ats-results-placeholder" data-aos="fade-right">
                <div className="ats-placeholder-icon">📊</div>
                <div className="ats-placeholder-title">Your ATS report will appear here</div>
                <div className="ats-placeholder-sub">
                  Upload your CV on the left and click Analyze to get your score,
                  keyword analysis, and personalized recommendations.
                </div>
              </div>
            )}

            {/* Loading */}
            {phase === "loading" && (
              <div className="ats-results-placeholder" data-aos="fade-right">
                <div className="ats-loading-ring" />
                <div className="ats-loading-title">Analyzing your CV with AI…</div>
                <div className="ats-loading-msg">{loadMsg}</div>
                <div className="ats-loading-note">This may take 15–30 seconds</div>
              </div>
            )}

            {/* Error */}
            {phase === "error" && (
              <div className="ats-results-placeholder ats-results-placeholder--error" data-aos="fade-right">
                <div className="ats-placeholder-icon" style={{ fontSize: "2.2rem" }}>⚠️</div>
                <div className="ats-error-title">Analysis Failed</div>
                <div className="ats-error-msg">{errMsg}</div>
                <button className="ats-error-btn" onClick={reset}>↺ Try Again</button>
              </div>
            )}

            {/* Results */}
            {phase === "results" && data && (
              <div className="ats-results" data-aos="fade-right">

                {/* Detected role / experience / source strip */}
                {(data.detectedRole || data.experienceYears || data._source) && (
                  <div className="ats-info-strip" data-aos="fade-up">
                    {data.detectedRole && (
                      <span className="ats-info-chip ats-info-chip--role">🎯 {data.detectedRole}</span>
                    )}
                    {data.experienceYears && (
                      <span className="ats-info-chip ats-info-chip--exp">📅 {data.experienceYears}</span>
                    )}
                    {data._source && (
                      <span
                        className="ats-info-chip"
                        style={
                          data._source === "backend"
                            ? { background: "var(--green-l)", borderColor: "var(--green-b)", color: "var(--green)" }
                            : { background: "var(--prime-l)", borderColor: "var(--border2)", color: "var(--prime)" }
                        }
                      >
                        {data._source === "backend" ? "⚡ Server Analysis" : "🤖 Direct AI Analysis"}
                      </span>
                    )}
                  </div>
                )}

                {/* Score banner */}
                <div className="ats-score-banner" data-aos="fade-up" data-aos-delay="60">
                  <ScoreRing score={data.score} />
                  <div className="ats-score-info">
                    <h2>{data.headline}</h2>
                    <p><HighlightedText text={data.description} /></p>
                    <div className="ats-pillars">
                      {data.pillars?.map((p, i) => (
                        <PillarBar key={p.name} name={p.name} val={p.val} delay={i * 150} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Top strength / critical fix */}
                {(data.topStrength || data.criticalFix) && (
                  <div className="ats-highlights-grid">
                    {data.topStrength && (
                      <div className="ats-highlight-card ats-highlight-card--strength" data-aos="fade-left">
                        <div className="ats-highlight-label">✦ Top Strength</div>
                        <div className="ats-highlight-text">{data.topStrength}</div>
                      </div>
                    )}
                    {data.criticalFix && (
                      <div className="ats-highlight-card ats-highlight-card--fix" data-aos="fade-right">
                        <div className="ats-highlight-label">⚡ Critical Fix</div>
                        <div className="ats-highlight-text">{data.criticalFix}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* What's working / Needs attention */}
                <div className="ats-section-heading" data-aos="fade-up">Detailed Findings</div>
                <div className="ats-grid-2">
                  <div className="ats-panel" data-aos="fade-left">
                    <div className="ats-panel-header">
                      <span className="ats-panel-icon">✅</span>
                      <span className="ats-panel-title">What's Working</span>
                      <span className="ats-panel-count ats-count-good">{data.good?.length} checks</span>
                    </div>
                    <div className="ats-panel-body">
                      {data.good?.map((g, i) => (
                        <div key={i} className="ats-check-item" style={{ animationDelay: `${i * 0.07}s` }}>
                          <div className="ats-check-icon ats-check-icon--ok">✓</div>
                          <div>
                            <div className="ats-check-text"><HighlightedText text={g.text} /></div>
                            <div className="ats-check-sub">{g.sub}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="ats-panel" data-aos="fade-right">
                    <div className="ats-panel-header">
                      <span className="ats-panel-icon">⚠️</span>
                      <span className="ats-panel-title">Needs Attention</span>
                      <span className="ats-panel-count ats-count-warn">{data.warnings?.length} issues</span>
                    </div>
                    <div className="ats-panel-body">
                      {data.warnings?.map((w, i) => (
                        <div key={i} className="ats-check-item" style={{ animationDelay: `${i * 0.07}s` }}>
                          <div className="ats-check-icon ats-check-icon--warn">!</div>
                          <div>
                            <div className="ats-check-text"><HighlightedText text={w.text} /></div>
                            <div className="ats-check-sub">{w.sub}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Missing elements */}
                <div className="ats-section-heading" data-aos="fade-up">Fix These to Boost Your Score</div>
                <div className="ats-panel" data-aos="fade-up">
                  <div className="ats-panel-header">
                    <span className="ats-panel-icon">🎯</span>
                    <span className="ats-panel-title">Missing ATS Elements</span>
                    <span className="ats-panel-count ats-count-bad">{data.missing?.length} missing</span>
                  </div>
                  <div className="ats-panel-body">
                    {data.missing?.map((m, i) => {
                      const open = !!expandedMissing[i];
                      return (
                        <div
                          key={i}
                          className="ats-missing-item"
                          onClick={() => toggleMissing(i)}
                          style={{ animationDelay: `${i * 0.08}s` }}
                        >
                          <div className="ats-missing-header">
                            <span className={`ats-missing-badge ${badgeClass(m.priority)}`}>{m.priority}</span>
                            <span className="ats-missing-name"><HighlightedText text={m.name} /></span>
                            <span className={`ats-missing-chevron${open ? " open" : ""}`}>▾</span>
                          </div>
                          {open && (
                            <div className="ats-missing-body">
                              <div className="ats-missing-desc"><HighlightedText text={m.desc} /></div>
                              <div className="ats-missing-tip">
                                <strong>💡 How to fix: </strong>
                                <HighlightedText text={m.tip} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Keyword intelligence */}
                <div className="ats-section-heading" data-aos="fade-up">Keyword Intelligence</div>
                <div className="ats-panel" data-aos="fade-up">
                  <div className="ats-panel-header">
                    <span className="ats-panel-icon">🔑</span>
                    <span className="ats-panel-title">Keyword Analysis</span>
                    <span className="ats-panel-meta">Extracted from your actual resume</span>
                  </div>
                  <div className="ats-panel-body">
                    <div className="ats-section-label" style={{ marginTop: 14 }}>
                      Detected in your CV ({data.keywordsFound?.length})
                    </div>
                    <div className="ats-keywords-wrap" style={{ marginBottom: 20 }}>
                      {data.keywordsFound?.map((k) => (
                        <Tooltip key={k} text={ATS_GLOSSARY[k] || `"${k}" was found in your resume.`}>
                          <span className="ats-kw-found">{k}</span>
                        </Tooltip>
                      ))}
                    </div>
                    <div className="ats-section-label">
                      Missing — add these ({data.keywordsMissing?.length})
                    </div>
                    <div className="ats-keywords-wrap">
                      {data.keywordsMissing?.map((k) => (
                        <Tooltip key={k} text={ATS_GLOSSARY[k] || `"${k}" is a high-value keyword — add it to your resume.`}>
                          <span className="ats-kw-missing">{k} <span className="ats-kw-add">+ add</span></span>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                </div>

                {/* CTA action panel */}
                <div className="ats-action-panel" data-aos="fade-up">
                  <div>
                    <h3>Apply the fixes & re-analyze</h3>
                    <p>Update your CV with the recommendations above, then run another check to watch your score climb.</p>
                  </div>
                  <div className="ats-action-btns">
                    <button className="ats-btn-outline" onClick={reset}>↺ New Analysis</button>
                    <button className="ats-btn-white" onClick={downloadReport}>⬇ Download Report</button>
                  </div>
                </div>

              </div>
            )}
          </div>
          {/* END RIGHT */}

        </div>
        {/* END TWO-COLUMN */}

      </div>
    </div>
  );
}
